/**
 * HVP Cascade Controller - Smart Cascade compatibility
 *
 * Validates a changed value against the selections BELOW it and refreshes
 * downstream OPTIONS while preserving still-valid VALUES.
 *
 * Split out of cascade-controller.js (350-line rule): the methods below moved
 * VERBATIM and are installed on HVPCascadeController.prototype by
 * cascade-controller.js, so `this` is the same instance and behavior is identical.
 *
 * @package HaruVariationsPro
 * @version 1.0.0
 */

export const CascadeCompatMethods = {
    /**
     * Check if current selection is compatible with below selections
     *
     * Builds a complete selections object including:
     * - Selections above the changed index
     * - The new value for the changed attribute
     * - Existing selections below the changed index
     *
     * Then validates this combination against available variations.
     *
     * @param {number} changedIndex - Index of the attribute being changed
     * @param {string} newValue - New value being selected
     * @returns {Promise<Object>} Compatibility result
     * @returns {boolean} result.isCompatible - Whether the combination is valid
     * @returns {Object|null} result.variation - Matching variation if found
     * @returns {Object} result.fullSelections - Complete selections object tested
     *
     * @example
     * // User changes pa_color (index 1) to 'blue'
     * // Checks if current pa_material selection is still valid
     * const result = await checkCompatibilityWithBelow(1, 'blue');
     * if (result.isCompatible) {
     *   // pa_material selection can be preserved
     * }
     */
    async checkCompatibilityWithBelow(changedIndex, newValue) {
        const start = performance.now();

        // Build full selections object
        const changedAttr = this.attributeOrder[changedIndex];
        const normalizedChanged = this.normalizeAttributeName(changedAttr);

        // Get selections above (unchanged)
        const selectionsAbove = this.getSelectionsAbove(changedIndex);

        // Get selections below (to check compatibility)
        const selectionsBelow = this.getSelectionsBelow(changedIndex);

        // Build complete selections: above + new + below
        const fullSelections = {
            ...selectionsAbove,
            [normalizedChanged]: newValue,
            ...selectionsBelow
        };

        this.log('Smart Cascade: Checking compatibility', {
            above: selectionsAbove,
            changed: { [normalizedChanged]: newValue },
            below: selectionsBelow,
            full: fullSelections
        });

        // Emit event for UI feedback
        this.emit('compatibilityCheckStart', {
            attribute: normalizedChanged,
            newValue: newValue,
            fullSelections: fullSelections
        });

        // Check cache first
        const cacheKey = this.cache.buildKey(this.productId, fullSelections, 'compat');
        const cached = this.cache.get(cacheKey);

        if (cached) {
            this.log('Smart Cascade: Compatibility result from cache');
            return cached;
        }

        try {
            let compatResult;

            // USE CLIENT-SIDE CASCADE IF AVAILABLE (instant, no AJAX)
            if (this.clientCascade && this.clientCascade.isAvailable()) {
                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] CLIENT-SIDE COMPATIBILITY CHECK - No AJAX');
                compatResult = this.clientCascade.checkCompatibility(fullSelections);
            } else {
                // FALLBACK: Server-side AJAX (slow)
                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] SERVER-SIDE COMPATIBILITY CHECK - Using AJAX (slow fallback)');
                const result = await this.api.checkCompatibility(this.productId, fullSelections);

                compatResult = {
                    isCompatible: result?.isCompatible ?? false,
                    variation: result?.variation ?? null,
                    fullSelections: fullSelections,
                    checkedAt: Date.now()
                };
            }

            // Cache the result
            this.cache.set(cacheKey, compatResult, 60); // Short TTL for compatibility checks

            const elapsed = Math.round(performance.now() - start);
            this.log(`Smart Cascade: Compatibility check completed in ${elapsed}ms`, compatResult);

            return compatResult;

        } catch (error) {
            console.error('[HVP] Smart Cascade compatibility check error:', error);

            // Return incompatible on error to fall back to traditional cascade
            return {
                isCompatible: false,
                variation: null,
                fullSelections: fullSelections,
                error: error.message
            };
        }
    },

    /**
     * Update dropdown OPTIONS for all attributes BELOW the given index
     *
     * This is used by Smart Cascade when a selection is compatible:
     * - We preserve the selected VALUES
     * - But we update the OPTIONS to show only valid choices
     *
     * This ensures dropdowns always show accurate options even when
     * selections are preserved.
     *
     * @param {number} fromIndex - Index of the changed attribute
     */
    async updateDownstreamDropdownOptions(fromIndex) {
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] ----------------------------------------');
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] UPDATING DOWNSTREAM DROPDOWN OPTIONS');
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] ----------------------------------------');
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] From index:', fromIndex);

        // Get options for all downstream attributes
        const downstreamOptions = {};

        for (let i = fromIndex + 1; i < this.attributeOrder.length; i++) {
            const attr = this.attributeOrder[i];
            const normalized = this.normalizeAttributeName(attr);

            // Get options based on selections ABOVE this attribute only
            const selectionsAbove = this.getSelectionsAbove(i);

            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Getting options for', normalized, 'based on:', JSON.stringify(selectionsAbove));

            let options = null;

            // USE CLIENT-SIDE CASCADE IF AVAILABLE (instant, no AJAX)
            if (this.clientCascade && this.clientCascade.isAvailable()) {
                options = this.clientCascade.getOptions(selectionsAbove, normalized);
            } else {
                // FALLBACK: Server-side (less efficient)
                try {
                    const result = await this.api.loadFilteredOptions(
                        this.productId,
                        selectionsAbove,
                        normalized
                    );
                    options = result?.options;
                } catch (error) {
                    console.error('[HVP DEBUG] Error loading options for', normalized, error);
                    continue;
                }
            }

            if (options && options.length > 0) {
                downstreamOptions[normalized] = options;
                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG]   -', normalized, ':', options.length, 'options');
            }
        }

        // Apply options to dropdowns while PRESERVING current values
        for (const [attr, options] of Object.entries(downstreamOptions)) {
            const dropdown = this.getDropdown(attr);
            if (!dropdown) continue;

            // Gate the update: skip dropdowns whose upstream attributes aren't all
            // selected yet. Without this, upstream-only cascade filtering can collapse
            // a deep attribute to a single option and visually pre-select it before
            // the user has chosen the intermediate ones.
            if (!this.areAllParentsSelected(attr)) {
                continue;
            }

            // Store current value before update
            const currentValue = dropdown.value;
            const currentSelection = this.selections[attr];

            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Updating', attr, '- current value:', currentValue);

            // Update dropdown options
            this.updateDropdownOptions(attr, options, false);

            // Restore selection if it's still valid
            if (currentValue && currentSelection) {
                const isStillValid = options.some(opt =>
                    (opt.value || opt.slug) === currentValue
                );

                if (isStillValid) {
                    dropdown.value = currentValue;
                    window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG]   Value preserved:', currentValue);
                } else {
                    // Value is no longer valid - clear it
                    dropdown.value = '';
                    delete this.selections[attr];
                    window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG]   Value NO LONGER VALID, cleared');
                }
            }
        }

        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Downstream options update complete');

        // Fire legacy event for turnaround-variations compatibility
        const updatedAttributes = Object.keys(downstreamOptions).map(attr => 'attribute_' + attr);
        if (updatedAttributes.length > 0) {
            this.fireLegacyUpdatedEvent(updatedAttributes);
        }

        // Auto-select any downstream dropdown that collapsed to a single option.
        // The Smart Cascade "compatible" branch calls this method and then returns
        // without running the traditional-cascade autoSelectSingleOptions path, so
        // a narrowed-to-one downstream (e.g. spot_high_gloss_uv_coating → "none")
        // would otherwise sit visually at "Choose an option" until the user clicks
        // through it. Also covers the case where preservation cleared an invalid
        // value and only one valid option remains.
        await this.autoSelectSingleOptions(fromIndex + 1);
    },
};

export default CascadeCompatMethods;
